import { createContext, forwardRef, memo, useContext, useEffect, useRef, useState } from 'react';

import { cn } from '../../lib/styles';

const TableRowContext = createContext<{ isInitialized: React.MutableRefObject<boolean> }>({
  isInitialized: { current: false },
});

const useFirstCell = () => {
  const { isInitialized } = useContext(TableRowContext);
  const [isFirstCell, setIsFirstCell] = useState(false);

  useEffect(() => {
    if (isInitialized.current) return;

    isInitialized.current = true;
    setIsFirstCell(true);
  }, [isInitialized]);

  return isFirstCell;
};

const TableCellPadders = () => {
  return (
    <>
      <div
        className={cn(
          'cell-padder absolute bg-bg-sur-primary h-14 w-4 top-0 left-[-16px] rounded-tl-sm rounded-bl-sm transition-all',
          'group-hover:bg-bg-sur-secondary',
        )}
      />
      <div
        className={cn(
          'cell-padder absolute bg-bg-sur-primary h-14 w-4 top-0 right-[-16px] rounded-tr-sm rounded-br-sm transition-all',
          'group-hover:bg-bg-sur-secondary',
        )}
      />
    </>
  );
};

const TableBase = ({ className, ...rest }: React.ComponentProps<'table'>, ref: React.Ref<HTMLTableElement>) => {
  return (
    <div data-slot="table-container" className="relative w-full">
      <table ref={ref} data-slot="table" className={cn('w-full caption-bottom text-sm', className)} {...rest} />
    </div>
  );
};

export const Table = memo(forwardRef(TableBase));

function TableHeader({ className, ...rest }: React.ComponentProps<'thead'>) {
  return (
    <thead
      data-slot="table-header"
      className={cn(
        '[&_tr]:border-b [&_tr]:border-border-secondary [&_tr]:hover:border-bg-sur-secondary',
        '[&_th:first-child]:pl-0',
        '[&_th:last-child]:pr-0',
        className,
      )}
      {...rest}
    />
  );
}

function TableBody({ className, ...rest }: React.ComponentProps<'tbody'>) {
  return <tbody data-slot="table-body" className={cn('[&_tr:last-child]:border-0', className)} {...rest} />;
}

function TableFooter({ className, ...props }: React.ComponentProps<'tfoot'>) {
  return (
    <tfoot
      data-slot="table-footer"
      className={cn('bg-bg-inter-secondary-default border-t font-medium [&>tr]:last:border-b-0', className)}
      {...props}
    />
  );
}

const TableRowBase = (
  { className, children, ...rest }: React.ComponentProps<'tr'>,
  ref: React.Ref<HTMLTableRowElement>,
) => {
  const isInitialized = useRef(false);

  return (
    <TableRowContext.Provider value={{ isInitialized }}>
      <tr
        ref={ref}
        data-slot="table-row"
        className={cn(
          'hover:bg-bg-sur-secondary data-[state=selected]:bg-bg-inter-secondary-active border-b border-border-tertiary hover:border-bg-sur-secondary transition-all',
          'relative group h-14',
          'data-[active=true]:[&_.cell-padder]:bg-bg-sur-secondary',
          '[&_td:first-child]:pl-0',
          '[&_td:last-child]:pr-0',
          className,
        )}
        {...rest}
      >
        {children}
      </tr>
    </TableRowContext.Provider>
  );
};

export const TableRow = memo(forwardRef(TableRowBase));

function TableHead({ className, children, ...rest }: React.ComponentProps<'th'>) {
  const isFirstCell = useFirstCell();

  return (
    <th
      data-slot="table-head"
      className={cn(
        'font-heading-l font-semibold text-text-primary h-10 px-2 text-left align-middle whitespace-nowrap [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
        className,
      )}
      {...rest}
    >
      {isFirstCell ? <TableCellPadders /> : null}

      {children}
    </th>
  );
}

function TableCell({ className, children, ...rest }: React.ComponentProps<'td'>) {
  const isFirstCell = useFirstCell();

  return (
    <td
      data-slot="table-cell"
      className={cn(
        'p-2 align-middle whitespace-nowrap [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
        className,
      )}
      {...rest}
    >
      {isFirstCell ? <TableCellPadders /> : null}

      {children}
    </td>
  );
}

function TableCaption({ className, ...props }: React.ComponentProps<'caption'>) {
  return <caption data-slot="table-caption" className={cn('text-text-tertiary mt-4 text-sm', className)} {...props} />;
}

export { TableHeader, TableBody, TableFooter, TableHead, TableCell, TableCaption };
